import { useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation } from 'react-router-dom';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HomeIcon from '@mui/icons-material/Home';
import StoreIcon from '@mui/icons-material/Store';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import BalanceIcon from '@mui/icons-material/Balance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ConstructionIcon from '@mui/icons-material/Construction';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import FlightIcon from '@mui/icons-material/Flight';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BoltIcon from '@mui/icons-material/Bolt';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ForumIcon from '@mui/icons-material/Forum';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import IosShareIcon from '@mui/icons-material/IosShare';
import LinkIcon from '@mui/icons-material/Link';
import ChatIcon from '@mui/icons-material/Chat';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useAuth } from '../../../../hooks/useAuth';
import { useHomeEquity } from '../../hooks/useHomeEquity';
import { IHomeEquity } from '../../types';
import { IMedal } from '../../../../interfaces/IMedals';
import { Notify } from '../../../../components/Notify';
import * as routes from '../../../../constants/Routes';
import { api } from '../../../../server/api';
import { CalculadoraComissao } from '../CalculadoraComissao/CalculadoraComissao';
import './HomeEquityDetails.css';

function AnimatedCounter({
  to,
  duration = 1.8,
}: {
  to: number;
  duration?: number;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          obs.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!triggered) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * to));
      if (progress < 1) requestAnimationFrame(step);
      else setValue(to);
    };
    requestAnimationFrame(step);
  }, [triggered, to, duration]);

  return <span ref={ref}>{value}</span>;
}

const PROFILE_MAP: {
  key: string;
  ico: ReactNode;
  tag: string;
  hl: boolean;
  label: string;
  desc: string;
}[] = [
  {
    key: 'apartment',
    ico: <ApartmentIcon sx={{ fontSize: 28 }} />,
    tag: 'RESIDENCIAL',
    hl: true,
    label: 'Dono de apartamento',
    desc: 'Usa o crédito para investir ou quitar dívidas.',
  },
  {
    key: 'house',
    ico: <HomeIcon sx={{ fontSize: 28 }} />,
    tag: 'CASA',
    hl: false,
    label: 'Dono de casa própria',
    desc: 'Crédito sem vender o imóvel.',
  },
  {
    key: 'commercial',
    ico: <StoreIcon sx={{ fontSize: 28 }} />,
    tag: 'COMERCIAL',
    hl: false,
    label: 'Dono de sala/loja',
    desc: 'Capital para expandir o negócio.',
  },
  {
    key: 'rural',
    ico: <AgricultureIcon sx={{ fontSize: 28 }} />,
    tag: 'RURAL',
    hl: false,
    label: 'Proprietário rural',
    desc: 'Crédito para negócios rurais.',
  },
  {
    key: 'rentOut',
    ico: <HolidayVillageIcon sx={{ fontSize: 28 }} />,
    tag: 'INQUILINO',
    hl: false,
    label: 'Proprietário que aluga',
    desc: 'Usa o patrimônio parado.',
  },
  {
    key: 'heir',
    ico: <BalanceIcon sx={{ fontSize: 28 }} />,
    tag: 'HERANÇA',
    hl: false,
    label: 'Herdeiro com imóvel',
    desc: 'Patrimônio que gera crédito.',
  },
];

const DEFAULT_TESTIMONIALS = [
  {
    name: 'Rodrigo Alves',
    city: 'São Paulo, SP',
    level: 'Ouro',
    text: 'No primeiro mês, fechei dois contratos. Em seis meses já estava no Ouro.',
    result: '+R$ 18.000 no último trimestre',
  },
  {
    name: 'Fernanda Melo',
    city: 'Belo Horizonte, MG',
    level: 'Diamante',
    text: 'Cheguei no Diamante em 8 meses. O programa representa mais de R$ 7.000/mês.',
    result: '+R$ 7.000 / mês',
  },
  {
    name: 'Carlos E. Santos',
    city: 'Curitiba, PR',
    level: 'Prata',
    text: 'Os clientes mesmos pedem depois que comparam as taxas.',
    result: '+R$ 9.400 em 6 meses',
  },
];

const DEFAULT_RATES = [
  {
    lbl: 'Cartão de crédito',
    rate: '~15% a.m.',
    bar: 100,
    variant: 'bad' as const,
  },
  {
    lbl: 'Empréstimo pessoal',
    rate: '~5% a.m.',
    bar: 65,
    variant: 'bad' as const,
  },
  {
    lbl: 'Consórcio',
    rate: '~1,5% a.m.',
    bar: 28,
    variant: 'neutral' as const,
  },
  {
    lbl: 'Home Equity',
    rate: 'a partir de 0,89%',
    bar: 12,
    variant: 'good' as const,
  },
];

const WHEN_ITEMS: { ico: ReactNode; txt: string }[] = [
  {
    ico: <CreditCardIcon sx={{ fontSize: 20 }} />,
    txt: 'Quer quitar dívidas caras',
  },
  {
    ico: <ConstructionIcon sx={{ fontSize: 20 }} />,
    txt: 'Quer reformar o imóvel',
  },
  {
    ico: <WorkIcon sx={{ fontSize: 20 }} />,
    txt: 'Precisa de capital de giro',
  },
  { ico: <HomeIcon sx={{ fontSize: 20 }} />, txt: 'Quer comprar outro imóvel' },
  { ico: <SchoolIcon sx={{ fontSize: 20 }} />, txt: 'Educação dos filhos' },
  { ico: <FlightIcon sx={{ fontSize: 20 }} />, txt: 'Projeto de vida' },
];

const SOCIAL_NETWORKS = [
  {
    lbl: 'WhatsApp',
    bg: '#25D366',
    href: (url: string) => `https://wa.me/?text=${encodeURIComponent(url)}`,
  },
  {
    lbl: 'Facebook',
    bg: '#1877F2',
    href: (url: string) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    lbl: 'Telegram',
    bg: '#2AABEE',
    href: (url: string) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}`,
  },
  {
    lbl: 'X',
    bg: '#000',
    href: (url: string) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
  },
];

export function HomeEquityDetails() {
  const { homeEquityId } = useParams<{ homeEquityId: string }>();
  const { state } = useLocation();
  const { client, player } = useAuth();
  const { getHomeEquityById } = useHomeEquity();

  const [he, setHe] = useState<IHomeEquity | null>(null);
  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(0);
  const [sheet, setSheet] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fctaShow, setFctaShow] = useState(false);
  const [fctaDism, setFctaDism] = useState(false);
  const [medalTiers, setMedalTiers] = useState<IMedal[]>([]);

  const trackRef = useRef<HTMLDivElement>(null);
  const [ratesVisible, setRatesVisible] = useState(false);

  useEffect(() => {
    if (sheet) {
      requestAnimationFrame(() => setSheetVisible(true));
    } else {
      setSheetVisible(false);
    }
  }, [sheet]);

  useEffect(() => {
    if (!homeEquityId || !client?.id) return;
    setLoading(true);
    getHomeEquityById(homeEquityId, client.id)
      .then((data) => setHe(data ?? null))
      .finally(() => setLoading(false));
  }, [homeEquityId, client?.id]);

  useEffect(() => {
    if (!client?.clientUrl) return;
    api
      .get(`/clients/${client.clientUrl}/medals-settings`)
      .then(({ data }) => {
        if (data.body?.tiers?.length) setMedalTiers(data.body.tiers);
      })
      .catch((err) => console.log('getMedalSettings error:', err));
  }, [client?.clientUrl]);

  const imgs = he?.medias?.images?.length ? he.medias.images : [];
  const nSlides = imgs.length;

  useEffect(() => {
    if (!nSlides) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % nSlides), 4500);
    return () => clearInterval(t);
  }, [nSlides]);

  useEffect(() => {
    if (trackRef.current && nSlides) {
      trackRef.current.scrollTo({
        left: slide * trackRef.current.offsetWidth,
        behavior: 'smooth',
      });
    }
  }, [slide]);

  useEffect(() => {
    const onScroll = () => {
      if (fctaDism) return;
      if (window.scrollY >= 350 && !fctaShow) setFctaShow(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fctaDism, fctaShow]);

  const ratesRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRatesVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
  }, []);

  const shareUrl: string =
    state?.shareUrl ??
    (player?.id
      ? `${window.location.origin}${routes.HOME_EQUITY}/${homeEquityId}/${player.id}`
      : `${window.location.origin}${routes.HOME_EQUITY}/${homeEquityId}`);

  const commissionRate = he?.broker?.commissionPercent ?? 2;
  const commissionPoints = he?.broker?.commissionPoints ?? 0;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      Notify('Link copiado com sucesso', 'success');
    } catch {
      Notify('Erro ao copiar link', 'error');
    }
  }, [shareUrl]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <p style={{ color: '#7A96BF', fontSize: 14 }}>Carregando...</p>
      </div>
    );
  }

  if (!he) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <p style={{ color: '#7A96BF', fontSize: 14 }}>
          Publicação não encontrada.
        </p>
      </div>
    );
  }

  const {
    settings,
    broker,
    product,
    rates,
    referred,
    brokerTestimonials,
    marketingIntegrations,
  } = he;
  const partnerName = settings.financialPartner ?? 'Home Equity';
  const playerName = player?.name;
  const playerAvatar = player?.photoURL;

  const heroPills: string[] = [];
  if (product?.minRate) heroPills.push(`✓ A partir de ${product.minRate}%/mês`);
  if (product?.maxTermMonths)
    heroPills.push(`✓ Prazo até ${product.maxTermMonths} meses`);
  if (product?.maxLtvPercent)
    heroPills.push(`✓ Até ${product.maxLtvPercent}% do imóvel`);

  const activeProfiles = PROFILE_MAP.filter(
    (p) =>
      referred?.targetProfiles?.[
        p.key as keyof typeof referred.targetProfiles
      ] !== false
  );

  const ratesData = [
    rates?.creditCard != null
      ? {
          lbl: 'Cartão de crédito',
          rate: `~${rates.creditCard}% a.m.`,
          bar: 100,
          variant: 'bad' as const,
        }
      : null,
    rates?.personalLoan != null
      ? {
          lbl: 'Empréstimo pessoal',
          rate: `~${rates.personalLoan}% a.m.`,
          bar: 65,
          variant: 'bad' as const,
        }
      : null,
    rates?.consortium != null
      ? {
          lbl: 'Consórcio',
          rate: `~${rates.consortium}% a.m.`,
          bar: 28,
          variant: 'neutral' as const,
        }
      : null,
    rates?.homeEquity != null
      ? {
          lbl: `Home Equity (${partnerName})`,
          rate: `a partir de ${rates.homeEquity}%`,
          bar: 12,
          variant: 'good' as const,
        }
      : null,
  ].filter(Boolean) as {
    lbl: string;
    rate: string;
    bar: number;
    variant: 'good' | 'bad' | 'neutral';
  }[];

  return (
    <>
      <div className="hed-app">
        {/* Marketing pixel */}
        {marketingIntegrations?.isIntegrationPixel &&
          marketingIntegrations.integrationPixelCode && (
            <img
              src={`https://www.facebook.com/tr?id=${marketingIntegrations.integrationPixelCode}&ev=PageView&noscript=1`}
              style={{ display: 'none' }}
              alt=""
            />
          )}

        {/* Hero */}
        <div className="hed-hero">
          <div className="hed-hero-z">
            <div className="hed-hero-badges">
              <div className="hed-logo-chip">
                <span
                  style={{ fontSize: 13, fontWeight: 900, color: '#0A1F5C' }}
                >
                  {partnerName}
                </span>
              </div>
              <div className="hed-hero-tag">PUBLICAÇÃO — HOME EQUITY</div>
            </div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: '#fff',
                marginBottom: 6,
              }}
            >
              {broker?.title}
            </h1>
            <p
              style={{
                fontSize: 12,
                color: 'rgba(255,255,255,.88)',
                lineHeight: 1.7,
                marginBottom: 14,
              }}
            >
              Compartilhe com clientes que possuem imóvel próprio e precisam de
              crédito com as melhores taxas do mercado.
            </p>
            {heroPills.length > 0 && (
              <div className="hed-hero-pills">
                {heroPills.map((p) => (
                  <div key={p} className="hed-pill">
                    {p}
                  </div>
                ))}
              </div>
            )}
          </div>

          {imgs.length > 0 && (
            <div className="hed-carousel">
              <div className="hed-c-track" ref={trackRef}>
                {imgs.map((src, i) => (
                  <div key={i} className="hed-c-slide">
                    <img src={src} alt="" />
                  </div>
                ))}
              </div>
              {nSlides > 1 && (
                <>
                  <button
                    className="hed-c-arrow"
                    style={{ left: 10 }}
                    onClick={() => setSlide((s) => Math.max(0, s - 1))}
                  >
                    ‹
                  </button>
                  <button
                    className="hed-c-arrow"
                    style={{ right: 10 }}
                    onClick={() =>
                      setSlide((s) => Math.min(nSlides - 1, s + 1))
                    }
                  >
                    ›
                  </button>
                  <div className="hed-c-dots">
                    {imgs.map((_, i) => (
                      <button
                        key={i}
                        className={`hed-c-dot${slide === i ? ' active' : ''}`}
                        onClick={() => setSlide(i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Outer components — constrained to match hed-desktop-wrap width */}
        <div className="hed-outer-section">
          {/* Alert box — only visible to corretor */}
          <div className="hed-alerta">
            <span style={{ fontSize: 22, flexShrink: 0 }}>🔐</span>
            <div>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: '#92400E',
                  marginBottom: 3,
                }}
              >
                Esta página é exclusiva para você, corretor.
              </p>
              <p style={{ fontSize: 11, color: '#78350F', lineHeight: 1.7 }}>
                O link que você compartilha leva o cliente para uma página
                diferente — sem informações do programa ou comissões.
              </p>
            </div>
          </div>

          {/* Approach argument */}
          {broker?.approachScript && (
            <div className="hed-argumento">
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#0A1F5C',
                  marginBottom: 10,
                }}
              >
                <LightbulbIcon
                  sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }}
                />
                Argumento de abordagem
              </p>
              <div className="hed-arg-inner">
                <p
                  style={{
                    fontSize: 12,
                    color: '#2C4A7C',
                    lineHeight: 1.9,
                    fontStyle: 'italic',
                  }}
                >
                  "{broker.approachScript}"
                </p>
              </div>
            </div>
          )}

          {/* Alert CTA */}
          {broker?.alertMessage && (
            <div
              style={{
                margin: '12px 14px 0',
                background: '#E8F8FF',
                border: '1.5px solid rgba(0,194,255,.25)',
                borderRadius: 16,
                padding: '14px 16px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <LightbulbIcon
                sx={{ fontSize: 22, flexShrink: 0, color: '#0A1F5C' }}
              />
              <p style={{ fontSize: 12, color: '#2C4A7C', lineHeight: 1.7 }}>
                {broker.alertMessage}
              </p>
            </div>
          )}

          {/* Blue CTA */}
          <div className="hed-cta-azul">
            <h2
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: '#fff',
                marginBottom: 5,
              }}
            >
              {broker?.ctaTitle ?? 'Seu cliente tem um imóvel?'}
            </h2>
            <p
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,.88)',
                marginBottom: 16,
              }}
            >
              {broker?.ctaSubtext ??
                'Apresente uma forma de financiamento que você e ele ganham!'}
            </p>
            <button
              className="hed-cta-azul-btn"
              onClick={() => setSheet(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <IosShareIcon sx={{ fontSize: 16 }} />
              INDICAR CLIENTE AGORA!
            </button>
          </div>

          {/* Benefits bar */}
          {heroPills.length > 0 && (
            <div className="hed-benefits">
              {product?.minRate && (
                <div className="hed-benefit">
                  <span
                    style={{
                      display: 'block',
                      marginBottom: 3,
                      color: '#0A1F5C',
                    }}
                  >
                    <PaidIcon sx={{ fontSize: 18 }} />
                  </span>
                  <span
                    style={{ fontSize: 9, color: '#7A96BF', display: 'block' }}
                  >
                    A partir de
                  </span>
                  <span
                    style={{ fontSize: 12, fontWeight: 800, display: 'block' }}
                  >
                    {product.minRate}%/mês
                  </span>
                </div>
              )}
              {product?.maxTermMonths && (
                <div className="hed-benefit">
                  <span
                    style={{
                      display: 'block',
                      marginBottom: 3,
                      color: '#0A1F5C',
                    }}
                  >
                    <HourglassBottomIcon sx={{ fontSize: 18 }} />
                  </span>
                  <span
                    style={{ fontSize: 9, color: '#7A96BF', display: 'block' }}
                  >
                    Prazo até
                  </span>
                  <span
                    style={{ fontSize: 12, fontWeight: 800, display: 'block' }}
                  >
                    {product.maxTermMonths} meses
                  </span>
                </div>
              )}
              {product?.maxLtvPercent && (
                <div className="hed-benefit">
                  <span
                    style={{
                      display: 'block',
                      marginBottom: 3,
                      color: '#0A1F5C',
                    }}
                  >
                    <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                  </span>
                  <span
                    style={{ fontSize: 9, color: '#7A96BF', display: 'block' }}
                  >
                    Crédito até
                  </span>
                  <span
                    style={{ fontSize: 12, fontWeight: 800, display: 'block' }}
                  >
                    {product.maxLtvPercent}% imóvel
                  </span>
                </div>
              )}
              {product?.approvalDays && (
                <div className="hed-benefit">
                  <span
                    style={{
                      display: 'block',
                      marginBottom: 3,
                      color: '#0A1F5C',
                    }}
                  >
                    <BoltIcon sx={{ fontSize: 18 }} />
                  </span>
                  <span
                    style={{ fontSize: 9, color: '#7A96BF', display: 'block' }}
                  >
                    Aprovação
                  </span>
                  <span
                    style={{ fontSize: 12, fontWeight: 800, display: 'block' }}
                  >
                    {product.approvalDays} dias úteis
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        {/* end hed-outer-section */}

        <div className="hed-sec">
          <div className="hed-desktop-wrap">
            {/* Rate comparison */}
            {(() => {
              const bars = ratesData.length > 0 ? ratesData : DEFAULT_RATES;
              const ltv = product?.maxLtvPercent ?? 60;
              const term = product?.maxTermMonths ?? 240;
              return (
                <div
                  style={{
                    background: '#0d1629',
                    borderRadius: 18,
                    padding: 20,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 16,
                      padding: 20,
                      marginBottom: 16,
                    }}
                  >
                    <h4
                      style={{
                        fontWeight: 700,
                        marginBottom: 20,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: '#f0f4ff',
                        fontSize: 15,
                      }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#00BFFF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                      </svg>
                      Comparativo de taxas
                    </h4>
                    <div
                      ref={ratesRef}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                      }}
                    >
                      {bars.map((b, idx) => (
                        <div key={b.lbl}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: 6,
                              fontSize: b.variant === 'good' ? 14 : 12,
                              fontWeight: b.variant === 'good' ? 700 : 400,
                              color:
                                b.variant === 'good'
                                  ? '#00BFFF'
                                  : 'rgba(240,244,255,0.6)',
                            }}
                          >
                            <span>{b.lbl}</span>
                            <span>{b.rate}</span>
                          </div>
                          <div
                            style={{
                              height: 12,
                              background: 'rgba(255,255,255,0.1)',
                              borderRadius: 999,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                borderRadius: 999,
                                width: ratesVisible ? `${b.bar}%` : '0%',
                                transition: `width 1.1s ease-out ${idx * 0.15}s`,
                                background:
                                  b.variant === 'good'
                                    ? 'linear-gradient(to right, #00BFFF, #67e8f9)'
                                    : b.variant === 'bad'
                                      ? 'rgba(248,113,113,0.7)'
                                      : 'rgba(255,255,255,0.3)',
                                boxShadow:
                                  b.variant === 'good'
                                    ? '0 0 8px rgba(0,191,255,0.6)'
                                    : 'none',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    style={{
                      background: 'rgba(0,191,255,0.08)',
                      border: '1px solid rgba(0,191,255,0.25)',
                      borderRadius: 16,
                      padding: 24,
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 52,
                        fontWeight: 900,
                        color: '#00BFFF',
                        lineHeight: 1.1,
                        marginBottom: 4,
                      }}
                    >
                      Até <AnimatedCounter to={ltv} duration={1.6} />%
                    </div>
                    <p
                      style={{
                        fontSize: 14,
                        color: '#f0f4ff',
                        margin: '0 0 16px',
                      }}
                    >
                      do valor do imóvel liberado em crédito
                    </p>
                    <div
                      style={{
                        borderTop: '1px solid rgba(0,191,255,0.2)',
                        margin: '0 0 16px',
                      }}
                    />
                    <div
                      style={{
                        fontSize: 44,
                        fontWeight: 900,
                        color: '#ffffff',
                        lineHeight: 1.1,
                        marginBottom: 4,
                      }}
                    >
                      <AnimatedCounter to={term} duration={2.0} /> meses
                    </div>
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#00BFFF',
                        margin: 0,
                      }}
                    >
                      para pagar — parcelas que cabem no bolso
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Explainer video */}
            {referred?.explainerVideoUrl && (
              <div className="hed-card">
                <div className="hed-cp" style={{ paddingBottom: 10 }}>
                  <h2
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginBottom: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <OndemandVideoIcon
                      sx={{ fontSize: 18, color: '#0A1F5C' }}
                    />
                    Assista antes de indicar
                  </h2>
                  <p style={{ fontSize: 12, color: '#7A96BF' }}>
                    Entenda o produto para explicar melhor ao cliente.
                  </p>
                </div>
                <div
                  className="hed-video-thumb"
                  onClick={() =>
                    window.open(referred.explainerVideoUrl, '_blank')
                  }
                >
                  <div
                    className="hed-video-bg"
                    style={{ backgroundImage: `url(${imgs[0] ?? ''})` }}
                  />
                  <div className="hed-video-ov">
                    <div className="hed-play-btn">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="white"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <p
                      style={{
                        color: 'rgba(255,255,255,.88)',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Assistir vídeo explicativo
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* How to use this publication */}
            <div className="hed-card">
              <div className="hed-cp">
                <h2
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    marginBottom: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: 18, color: '#0A1F5C' }} />
                  Como usar esta publicação
                </h2>
                <div style={{ position: 'relative' }}>
                  <div className="hed-step-line" />
                  {[
                    {
                      n: '01',
                      ico: <LinkIcon sx={{ fontSize: 18, color: '#fff' }} />,
                      t: 'Copie o link personalizado',
                      d: 'Use o botão "Indicar cliente agora" para copiar ou compartilhar seu link exclusivo.',
                    },
                    {
                      n: '02',
                      ico: <ChatIcon sx={{ fontSize: 18, color: '#fff' }} />,
                      t: 'Envie ao cliente',
                      d: 'Mande pelo WhatsApp, e-mail ou compartilhe nas outras redes o seu link personalizado —  o cliente acessa uma página sem dados de comissão.',
                    },
                    {
                      n: '03',
                      ico: (
                        <AssignmentIcon sx={{ fontSize: 18, color: '#fff' }} />
                      ),
                      t: 'Cliente preenche a simulação',
                      d: 'Ele informa nome, contato e realiza a simulação. O lead já chega com o seu ID vinculado.',
                    },
                    {
                      n: '04',
                      ico: (
                        <EmojiEventsIcon sx={{ fontSize: 18, color: '#fff' }} />
                      ),
                      t: 'Você recebe a comissão',
                      d: `Contrato aprovado = a partir de ${commissionRate}% do valor${commissionPoints > 0 ? ` + ${commissionPoints} pontos` : ''} creditados para você.`,
                    },
                  ].map((s, i, arr) => (
                    <div key={s.n} className="hed-step-row">
                      <div
                        className={`hed-s-dot ${i === arr.length - 1 ? 'hed-s-dot-gn' : 'hed-s-dot-br'}`}
                      >
                        {s.ico}
                      </div>
                      <div style={{ paddingTop: 8 }}>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: '#7A96BF',
                            letterSpacing: 1,
                            display: 'block',
                            marginBottom: 2,
                          }}
                        >
                          {s.n}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            display: 'block',
                            marginBottom: 3,
                          }}
                        >
                          {s.t}
                        </span>
                        <p
                          style={{
                            fontSize: 11,
                            color: '#2C4A7C',
                            lineHeight: 1.7,
                          }}
                        >
                          {s.d}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Target profiles */}
            {activeProfiles.length > 0 && (
              <div className="hed-card">
                <div className="hed-cp">
                  <h2
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginBottom: 5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <TrackChangesIcon sx={{ fontSize: 18, color: '#0A1F5C' }} />
                    Para quem indicar?
                  </h2>
                  <p
                    style={{ fontSize: 12, color: '#7A96BF', marginBottom: 13 }}
                  >
                    Qualquer pessoa com imóvel no nome — residencial, comercial,
                    rural ou de herança.
                  </p>
                  <div className="hed-profiles">
                    {activeProfiles.map((p) => (
                      <div
                        key={p.key}
                        className={p.hl ? 'hed-pcard-hl' : 'hed-pcard-nm'}
                      >
                        <span
                          style={{
                            display: 'block',
                            marginBottom: 7,
                            color: '#0A1F5C',
                          }}
                        >
                          {p.ico}
                        </span>
                        <span
                          style={{
                            display: 'inline-block',
                            borderRadius: 100,
                            padding: '2px 8px',
                            fontSize: 8,
                            fontWeight: 700,
                            background: p.hl ? '#0A1F5C' : '#C8D9F0',
                            color: p.hl ? '#fff' : '#7A96BF',
                            marginBottom: 6,
                          }}
                        >
                          {p.tag}
                        </span>
                        <p
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            marginBottom: 3,
                          }}
                        >
                          {p.label}
                        </p>
                        <p
                          style={{
                            fontSize: 10,
                            color: '#7A96BF',
                            lineHeight: 1.6,
                          }}
                        >
                          {p.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* When to indicate */}
            <div className="hed-card">
              <div className="hed-cp">
                <h2
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    marginBottom: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <BoltIcon sx={{ fontSize: 18, color: '#0A1F5C' }} />
                  Quando indicar?
                </h2>
                <div className="hed-when-grid">
                  {WHEN_ITEMS.map(({ ico, txt }) => (
                    <div
                      key={txt}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        padding: '11px 12px',
                        background: '#F0F4F8',
                        border: '1px solid #C8D9F0',
                        borderRadius: 12,
                      }}
                    >
                      <span
                        style={{
                          flexShrink: 0,
                          color: '#0A1F5C',
                          display: 'flex',
                        }}
                      >
                        {ico}
                      </span>
                      <p
                        style={{
                          fontSize: 11,
                          color: '#2C4A7C',
                          lineHeight: 1.5,
                        }}
                      >
                        {txt}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Commission level calculator */}
            <div
              className="hed-card"
              style={{ padding: 0, overflow: 'hidden' }}
            >
              <CalculadoraComissao
                tiers={medalTiers}
                commissionRate={commissionRate}
              />
            </div>

            {/* Broker testimonials */}
            {(() => {
              const testimonials = brokerTestimonials?.length
                ? brokerTestimonials
                : DEFAULT_TESTIMONIALS;
              return (
                <div style={{ marginBottom: 14 }}>
                  <h2
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      marginBottom: 5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <ForumIcon sx={{ fontSize: 18, color: '#0A1F5C' }} />O que
                    dizem nossos parceiros
                  </h2>
                  <p
                    style={{ fontSize: 12, color: '#7A96BF', marginBottom: 14 }}
                  >
                    Histórias reais de parceiros que aumentaram sua renda com o
                    programa.
                  </p>
                  <div className="hed-deps-grid">
                    {testimonials.map((t, i) => (
                      <div key={i} className="hed-dep">
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 10,
                          }}
                        >
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 800 }}>
                              {t.name}
                            </p>
                            {t.city && (
                              <p
                                style={{
                                  fontSize: 10,
                                  color: '#7A96BF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <LocationOnIcon sx={{ fontSize: 11 }} />
                                {t.city}
                              </p>
                            )}
                          </div>
                          {t.level && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#F59E0B',
                                background: '#FFFBEB',
                                borderRadius: 100,
                                padding: '3px 10px',
                              }}
                            >
                              {t.level}
                            </span>
                          )}
                        </div>
                        <p
                          style={{
                            fontSize: 12,
                            color: '#2C4A7C',
                            lineHeight: 1.8,
                            fontStyle: 'italic',
                            marginBottom: t.result ? 9 : 0,
                          }}
                        >
                          "{t.text}"
                        </p>
                        {t.result && (
                          <div
                            style={{
                              padding: '7px 11px',
                              background: '#DCFCE7',
                              border: '1px solid rgba(22,163,74,.2)',
                              borderRadius: 10,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#15803D',
                              }}
                            >
                              {t.result}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Final CTA */}
            <div className="hed-cta-final">
              <p
                style={{
                  fontSize: 17,
                  fontWeight: 900,
                  color: '#fff',
                  marginBottom: 5,
                }}
              >
                Pronto para indicar?
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,.85)',
                  marginBottom: 16,
                  lineHeight: 1.7,
                }}
              >
                Compartilhe o link com seu cliente e ganhe {commissionRate}% do
                contrato aprovado
                {commissionPoints > 0 ? ` + ${commissionPoints} pontos` : ''}.
              </p>
              <button
                className="hed-cta-final-btn"
                onClick={() => setSheet(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <IosShareIcon sx={{ fontSize: 16 }} />
                INDICAR CLIENTE AGORA!
              </button>
            </div>

            <div style={{ textAlign: 'center', paddingBottom: 14 }}>
              <p style={{ fontSize: 10, color: '#7A96BF' }}>
                © {new Date().getFullYear()} {client?.name ?? 'OQ Digital'} —
                Todos os direitos reservados
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating CTA */}
      {createPortal(
        <div className={`hed-fcta${fctaShow ? ' show' : ''}`}>
          {playerAvatar && (
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                overflow: 'hidden',
                flexShrink: 0,
                border: '2px solid #00C2FF',
              }}
            >
              <img
                src={playerAvatar}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                alt=""
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#0A1F5C',
                lineHeight: 1.3,
                marginBottom: 2,
              }}
            >
              Indicar cliente agora!
            </p>
            <span style={{ fontSize: 10, color: '#0057A8', fontWeight: 600 }}>
              Copie o link · {commissionRate}% de comissão
            </span>
          </div>
          <button className="hed-fcta-btn" onClick={() => setSheet(true)}>
            Indicar agora
          </button>
          <button
            onClick={() => {
              setFctaDism(true);
              setFctaShow(false);
            }}
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#F0F4F8',
              border: 'none',
              cursor: 'pointer',
              fontSize: 11,
              color: '#7A96BF',
              flexShrink: 0,
              fontWeight: 700,
              fontFamily: 'inherit',
            }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}

      {/* Share sheet */}
      {sheet &&
        createPortal(
          <>
            <div className="hed-overlay" onClick={() => setSheet(false)} />
            <div className={`hed-sheet${sheetVisible ? ' open' : ''}`}>
              <div
                style={{
                  display: 'none',
                }}
              />
              <div className="hed-sh-head">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1,
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}
                  >
                    {partnerName}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,.8)',
                    letterSpacing: 1,
                    marginBottom: 3,
                  }}
                >
                  LINK DO CLIENTE
                </p>
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: '#fff',
                    marginBottom: 10,
                  }}
                >
                  Compartilhe com seu indicado
                </p>
                {playerName && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 9 }}
                  >
                    {playerAvatar && (
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '2px solid rgba(255,255,255,.3)',
                        }}
                      >
                        <img
                          src={playerAvatar}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          alt=""
                        />
                      </div>
                    )}
                    <div>
                      <p
                        style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}
                      >
                        {playerName}
                      </p>
                      <p
                        style={{ fontSize: 10, color: 'rgba(255,255,255,.7)' }}
                      >
                        Link personalizado ativo
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding: 18 }}>
                <p
                  style={{
                    fontSize: 11,
                    color: '#7A96BF',
                    marginBottom: 10,
                    fontWeight: 600,
                  }}
                >
                  Compartilhar via
                </p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                  {SOCIAL_NETWORKS.map((r) => (
                    <a
                      key={r.lbl}
                      href={r.href(shareUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        flex: 1,
                        textDecoration: 'none',
                      }}
                    >
                      <div
                        className="hed-rede-ico"
                        style={{ background: r.bg }}
                      >
                        <span
                          style={{
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {r.lbl[0]}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: '#7A96BF',
                          fontFamily: 'inherit',
                        }}
                      >
                        {r.lbl}
                      </span>
                    </a>
                  ))}
                </div>
                <p
                  style={{
                    fontSize: 11,
                    color: '#7A96BF',
                    marginBottom: 7,
                    fontWeight: 600,
                  }}
                >
                  Ou copie o link direto
                </p>
                <div className="hed-link-box">
                  <span
                    style={{
                      flex: 1,
                      fontSize: 11,
                      color: '#7A96BF',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {shareUrl}
                  </span>
                  <button className="hed-copy-btn" onClick={handleCopy}>
                    {copied ? (
                      <CheckIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    )}
                  </button>
                </div>
                <button
                  className="hed-divulgar"
                  onClick={() => setSheet(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <IosShareIcon sx={{ fontSize: 16 }} />
                  DIVULGAR AGORA
                </button>
                <div className="hed-comm-sum">
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#0A1F5C',
                      marginBottom: 3,
                    }}
                  >
                    <EmojiEventsIcon
                      sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }}
                    />
                    Sua comissão por indicação
                  </p>
                  <p
                    style={{ fontSize: 22, fontWeight: 900, color: '#0A1F5C' }}
                  >
                    {commissionRate}% do contrato
                  </p>
                  {commissionPoints > 0 && (
                    <p style={{ fontSize: 11, color: '#7A96BF', marginTop: 2 }}>
                      + {commissionPoints} pontos no programa
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: 10,
                      color: '#7A96BF',
                      marginTop: 3,
                      fontStyle: 'italic',
                    }}
                  >
                    Ex: R$ 400k → R${' '}
                    {((400000 * commissionRate) / 100).toLocaleString('pt-BR')}{' '}
                    de comissão
                  </p>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
